import React, { useEffect, useState } from 'react';
import {
  Animated, Dimensions, Image, Platform, StyleSheet, Text, TouchableOpacity, View, ScrollView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { ActivityIndicator } from 'react-native-paper';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import DownArrow24 from '../24pxIcons/DownArrow24';
import EditIcon from '../EditIcon';
import MonitorIcon from '../MonitorIcon';
import UserRowMobile from './UserRowMobile';
import UserRowWeb from './UserRowWeb';
import GroupEditModal from './GroupEditModal';
import NotificationGroupCard from './NotificationGroupCard';
import getUsers from '../../requests/Users/GetUsers';
import getNotificationGroups from '../../requests/Users/GetNotificationGroups';
import AlertModal from '../AlertModal';
import { logEvent } from '../LogEvent';
import PressableStyled from '../PressableStyled';
import isMobile from '../IsMobile';
import { SAVE_USER_GROUP_SUCCESS } from '../../constants/AlertMessages';
import { UserPermission } from '../../constants/Types';

export default function UserGroupCard({
  title,
  description,
  groupId,
  userCount,
  allUsers,
  updateUsers,
  searchValue,
  deletedGroup,
}: {
  title: string;
  description: string;
  groupId: string;
  userCount: number;
  allUsers: any[];
  updateUsers(): void;
  searchValue: string;
  deletedGroup(): void;
}) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_UPDATE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [membersSelected, setMembersSelected] = useState(true);
  const [users, setUsers] = useState([]);
  const [notificationGroups, setNotificationGroups] = useState(undefined);
  const window = Dimensions.get('window');
  const [dimensions] = useState({ window });
  const [usersLoading, setUsersLoading] = useState(false);
  const renderMobile = (): boolean => Platform.OS !== 'web' || dimensions.window?.width < 500 || dimensions.window?.height < 500;
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertError, setAlertError] = useState(true);
  const [alertTitle, setAlertTitle] = useState('');

  useEffect(() => {
    if (drawerOpen) {
      getUsers({ userGroupId: groupId }).then((response) => {
        if (response?.items?.length > 0) {
          setUsers(response.items.filter((item) => item.userGroupIds.includes(groupId)));
        }
        setUsersLoading(false);
      });

      getNotificationGroups(groupId).then((response) => {
        if (response?.Prototypes?.length > 0) {
          setNotificationGroups(response.Prototypes);
        }
      });
    }
  }, [drawerOpen, usersLoading]);

  const showAlert = (newTitle: string, error: boolean = true) => {
    setAlertTitle(newTitle);
    setAlertError(error);
    setAlertVisible(true);
  };

  const renderUserIcon = (imageSource, facilityManager = false) => {
    if (imageSource === undefined) {
      return null;
    }
    return (
      <View
        style={{
          width: 48,
          height: 48,
          backgroundColor: facilityManager ? '#fff' : 'transparent',
          borderWidth: 1.5,
          borderColor: facilityManager ? Colors.light.hereworksBlue500 : 'transparent',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 24,
          marginLeft: 14,
        }}
      >
        <Image
          style={styles.userPic}
          source={{
            uri: imageSource,
          }}
        />
      </View>
    );
  };

  const updatedGroup = () => {
    setModalVisible(false);
    setUsersLoading(true);
    setUsers([]);
    setUsersLoading(true);
    updateUsers();
    showAlert(SAVE_USER_GROUP_SUCCESS, false);
  };

  const editGroupButton = () => canUpdateUserGroups && (
    <TouchableOpacity
      style={{
        borderWidth: 1,
        borderColor: Colors.light.hereworksBlue500,
        borderRadius: 4,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: renderMobile() ? 20 : 24,
        marginBottom: 24,
        marginTop: renderMobile() ? 0 : 24,
      }}
      onPress={() => {
        setModalVisible(true);
        logEvent('UserGroupCard', 'Edit_group');
      }}
      testID='edit-user-group-btn'
    >
      <GroupEditModal
        users={allUsers}
        {...{
          title,
          description,
          groupId,
          modalVisible,
          setModalVisible,
          updatedGroup,
          notificationGroups,
        }}
        groupDeleted={deletedGroup}
      />
      <Text
        style={{
          marginLeft: 24,
          marginRight: 8,
          marginVertical: 8,
          fontFamily: 'Roboto_400Regular',
          fontSize: 12,
          lineHeight: 20,
          color: Colors.light.hereworksBlue500,
        }}
      >
        Edit group
      </Text>
      <EditIcon style={{ marginRight: 24 }} />
    </TouchableOpacity>
  );

  return (
    <View style={[styles.groupCard, { marginHorizontal: renderMobile() ? 24 : 40 }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View
          style={{
            backgroundColor: Colors.light.hereworksDarkBlue,
            width: 48,
            height: 48,
            borderRadius: 24,
            marginLeft: 20,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MonitorIcon color={'#fff'} />
        </View>
        <View style={{ flex: 1, marginVertical: 17, marginRight: isMobile ? 42 : 0 }}>
          <Text style={styles.groupName} selectable>{title}</Text>
          <Text style={styles.groupDescription}>{description}</Text>
        </View>
        {!renderMobile() && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: 72,
            }}
          >
            <View style={{ flexDirection: 'row' }}>
              {users.map((user, key) => (
                <View key={key}>{renderUserIcon(user.icon, user.facilityManager)}</View>
              ))}
              <View
                style={{
                  width: 48,
                  height: 48,
                  backgroundColor: 'transparent',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginLeft: 8,
                }}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: Colors.light.hereworksBlue50,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: 'Roboto_400Regular',
                      color: Colors.light.hereworksDarkBlue,
                    }}
                  >
                    {userCount ?? 0}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        )}
        <PressableStyled
          style={{ position: 'absolute', right: 20 }}
          onPress={() => {
            setDrawerOpen(!drawerOpen);
            logEvent('UserGroupCard', `Drawer_${drawerOpen ? 'Close' : 'Open'}`);
          }}
          testID='um-expand-members-user-group-btn'
        >
          <DownArrow24
            style={[
              drawerOpen
                ? { transform: [{ rotateZ: '180deg' }] }
                : { transform: [{ rotateZ: '0deg' }] },
            ]}
          />
        </PressableStyled>
      </View>
      {renderMobile() && <View>{editGroupButton()}</View>}
      <Animated.View style={{ backgroundColor: 'transparent' }}>
        {drawerOpen && (
          <Animatable.View animation={'fadeIn'}>
            {usersLoading && <ActivityIndicator size={'large'} />}
            {renderMobile() ? <View style={{
              flexDirection: 'row',
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderColor: Colors.light.hereworksWhite500,
            }}>
              <PressableStyled
                style={{
                  backgroundColor: membersSelected ? Colors.light.hereworksWhite500 : '#fff',
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  flex: 1,
                }}
                onPress={() => {
                  if (!membersSelected) {
                    setMembersSelected(true);
                  }
                }}
              >
                <Text style={styles.mobileTab}>Members</Text>
              </PressableStyled>
              <PressableStyled
                style={{
                  backgroundColor: !membersSelected ? Colors.light.hereworksWhite500 : '#fff',
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  flex: 1,
                }}
                onPress={() => {
                  if (membersSelected) {
                    setMembersSelected(false);
                  }
                }}
              >
                <Text style={styles.mobileTab}>Notifications</Text>
              </PressableStyled>
            </View> : (
              <View>
                <View
                  style={{
                    height: 1,
                    backgroundColor: Colors.light.hereworksWhite500,
                    marginBottom: 10,
                  }}
                />
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={styles.title}>{membersSelected ? 'Members' : 'Notifications'}</Text>
                  <View
                    style={{
                      flex: 1,
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                      marginRight: 24,
                    }}
                  >
                    <TouchableOpacity
                      style={{
                        backgroundColor: membersSelected ? Colors.light.hereworksBlue500 : '#fff',
                        borderColor: membersSelected
                          ? Colors.light.hereworksBlue500
                          : Colors.light.hereworksBlue300,
                        borderWidth: 1,
                        marginRight: 8,
                        paddingHorizontal: 12,
                        paddingVertical: 4,
                        borderRadius: 4,
                      }}
                      onPress={() => {
                        if (!membersSelected) {
                          setMembersSelected(true);
                        }
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: 'Roboto_500Medium',
                          fontSize: 12,
                          lineHeight: 20,
                          color: membersSelected ? '#fff' : Colors.light.hereworksBlue400,
                        }}
                      >
                        MEMBERS
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={{
                        backgroundColor: !membersSelected ? Colors.light.hereworksBlue500 : '#fff',
                        borderColor: !membersSelected
                          ? Colors.light.hereworksBlue500
                          : Colors.light.hereworksBlue300,
                        borderWidth: 1,
                        marginRight: 8,
                        paddingHorizontal: 12,
                        paddingVertical: 4,
                        borderRadius: 4,
                      }}
                      onPress={() => {
                        if (membersSelected) {
                          setMembersSelected(false);
                        }
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: 'Roboto_500Medium',
                          fontSize: 12,
                          lineHeight: 20,
                          color: !membersSelected ? '#fff' : Colors.light.hereworksBlue400,
                        }}
                      >
                        NOTIFICATIONS
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
                {membersSelected && (
                  <View
                    style={{
                      flexDirection: 'row',
                      marginVertical: 12,
                      alignSelf: 'stretch',
                    }}
                  >
                    <Text style={[styles.headerText, { marginLeft: 40, flex: 1 }]}>MEMBER</Text>
                    <Text style={[styles.headerText, { marginLeft: 40, flex: 1 }]}>CONTACTS</Text>
                    <View style={{ flex: 1 }}>
                      <Text
                        style={[
                          styles.headerText,
                          {
                            alignSelf: 'flex-end',
                            marginRight: 28,
                          },
                        ]}
                      >
                        LAST ACTIVITY
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            )}
            {membersSelected
              && users
                // eslint-disable-next-line max-len
                ?.filter((user) => (user.firstName ?? '').toLowerCase().includes(searchValue.toLowerCase()))
                .map((user, userKey) => (
                  <View key={userKey}>
                    <View
                      style={{
                        flexDirection: 'row',
                        marginVertical: 2,
                        marginHorizontal: 24,
                        alignItems: 'center',
                        backgroundColor:
                          user.facilityManager && !renderMobile()
                            ? Colors.light.hereworksWhite100
                            : '#fff',
                        borderRadius: 10,
                      }}
                    >
                      {renderMobile() && (
                        <UserRowMobile
                          user={user}
                          groupName={title}
                          deleteAction={() => setUsers(users.filter((i) => i !== user))}
                        />
                      )}
                      {!renderMobile() && <UserRowWeb {...{ user }} />}
                    </View>
                  </View>
                ))}
            {!membersSelected && (
              <View style={{ marginVertical: isMobile ? 0 : 20 }}>
                <ScrollView contentContainerStyle={{
                  flexGrow: 1,
                  paddingHorizontal: isMobile ? 0 : 24,
                }}>
                  {notificationGroups ? notificationGroups.map((notificationGroup, index) => {
                    let match = false;
                    notificationGroup.Issues.forEach((issue) => {
                      if (issue.UserGroupId.includes(groupId)) {
                        match = true;
                      }
                    });
                    if (!match) {
                      return null;
                    }
                    return (
                      <View key={`${notificationGroup.Id ?? index}`}>
                        <NotificationGroupCard
                          title={notificationGroup.Name}
                          issues={notificationGroup.Issues}
                          groupId={groupId}
                        />
                      </View>
                    );
                  }) : <Text style={{
                    fontFamily: 'Roboto_500Medium',
                    fontSize: 12,
                    lineHeight: 20,
                    color: Colors.light.hereworksBlack400,
                    paddingLeft: 20,
                  }}>
                    {notificationGroups === undefined ? 'Loading data' : 'No linked notifications'}
                  </Text>}
                </ScrollView>
              </View>
            )}
            {!renderMobile() && membersSelected && editGroupButton()}
          </Animatable.View>
        )}
      </Animated.View>
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        setModalVisible={setAlertVisible}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  groupCard: {
    backgroundColor: '#fff',
    alignSelf: 'stretch',
    marginVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
  },
  groupName: {
    fontSize: 18,
    fontFamily: 'Poppins_600SemiBold',
    marginLeft: 12,
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontFamily: 'Poppins_500Medium>',
    marginLeft: 24,
    color: Colors.light.hereworksBlack500,
  },
  userPic: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 15,
    letterSpacing: 0.7,
    color: Colors.light.hereworksBlack400,
  },
  groupDescription: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    marginLeft: 12,
    flex: 1,
  },
  mobileTab: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
  },
});
